// @flow

import * as React from "react";

import classNames from "classnames";
import { identity, propEq, propOr } from "ramda";
import Downshift from "downshift";
import { useRafLoop } from "react-use";

import { useI18n } from "@quantum/i18n";

import { Icon, SearchIcon, ClickOutside } from "@quantum/ui";

import { nomeCorresponde } from "../../model";
import { useScrollArea } from "../comum/Scroll";

import Portal from "./Portal";

import "./SingleSelect.css";

const stateReducer = (state, changes) => {
  switch (changes.type) {
    case Downshift.stateChangeTypes.mouseUp:
      return {
        ...changes,
        inputValue: "",
        isOpen: changes.isOpen,
      };

    case Downshift.stateChangeTypes.keyDownEnter:
    case Downshift.stateChangeTypes.clickItem: {
      return {
        ...changes,
        inputValue: "",
        isOpen: changes.isOpen,
        highlightedIndex: state.highlightedIndex,
      };
    }

    case Downshift.stateChangeTypes.blurInput:
      return {
        ...changes,
        inputValue: "",
      };

    default:
      return changes;
  }
};

type Option = {
  id: string,
  value: string,
};

type Props = {
  alwaysOpen?: ?boolean,
  focused?: ?boolean,
  className?: ?string,
  defaultOption?: $PropertyType<Option, "value">,
  disabled?: boolean,
  open?: boolean,
  options: Array<Option>,
  portal?: boolean,
  searchable?: ?boolean,
  value: $PropertyType<Option, "id">,
  onChange: ($PropertyType<Option, "id">) => mixed,
  onOpen?: () => void,
  onClose?: () => void,
};

export const SingleSelect = ({
  alwaysOpen = false,
  focused = true,
  className,
  defaultOption,
  disabled,
  options = [],
  value = "",
  onChange = identity,
  open = false,
  portal = true,
  searchable = true,
  onOpen: onOpenCallback,
  onClose: onCloseCallback,
}: Props) => {
  const selectRef = React.useRef();
  const dropdown = React.useRef({ top: 0, left: 0, width: 0 });

  useRafLoop(() => {
    if (selectRef.current) {
      const { bottom, left, width } = selectRef.current.getBoundingClientRect();
      dropdown.current = {
        top: bottom + (window.scrollY || window.pageYOffset),
        left: left + (window.scrollX || window.pageXOffset),
        width: `${width}px`,
      };
    }
  }, [selectRef, dropdown]);

  const { translate } = useI18n();
  const [openedState, setOpen] = React.useState(false);
  const opened = alwaysOpen || openedState || open;

  const openState = React.useCallback(() => setOpen(true), []);
  const closeState = React.useCallback(() => setOpen(false), []);

  const onOpen = onOpenCallback || openState;
  const onClose = onCloseCallback || closeState;
  const onToggle = React.useCallback(() => (opened ? onClose() : onOpen()), [
    opened,
    onClose,
    onOpen,
  ]);

  const onChangeHandler = React.useCallback(
    selection => {
      if (!selection) return;
      onClose();
      onChange(selection.id);
    },
    [onChange, onClose]
  );

  const allOptions = defaultOption
    ? [{ id: "", value: defaultOption }, ...options]
    : options;

  const {
    registerScrollArea,
    registerScrollTarget,
    scrollTo,
  } = useScrollArea();

  React.useEffect(() => {
    if (value) scrollTo(value);
  }, [scrollTo, value]);

  const SelectBody: any = React.useCallback(
    ({ children }: any) => (portal ? <Portal>{children}</Portal> : children),
    [portal]
  );

  return (
    <Downshift
      isOpen={opened}
      stateReducer={stateReducer}
      onChange={onChangeHandler}
      itemToString={item => (item ? String(item.id) : "")}
    >
      {({
        getInputProps,
        getItemProps,
        getMenuProps,
        isOpen,
        inputValue,
        highlightedIndex,
        selectedItem,
      }) => (
        <div
          className={classNames(
            "q-Select",
            "q-Select--single",
            alwaysOpen && "q-Select--always-open",
            className
          )}
        >
          <div
            ref={selectRef}
            className={classNames("q-SelectOuterInput", {
              "q-SelectOuterInput--open": opened,
              "q-SelectOuterInput--disabled": disabled,
            })}
            onClick={opened || disabled ? identity : onOpen}
          >
            <p className={classNames("q-SelectOuterInput__value")}>
              {value
                ? propOr(
                    translate("configuracaoRelatorio.placeholderSelect"),
                    "value",
                    options.find(propEq("id", value))
                  )
                : translate("configuracaoRelatorio.placeholderSelect")}
            </p>

            <button
              className="q-SelectOuterInput__toggle-chevron"
              disabled={disabled}
              type="button"
              onClick={onToggle}
            >
              <Icon type={opened ? "chevron-up" : "chevron-down"} />
            </button>
          </div>

          {opened && (
            <SelectBody>
              <ClickOutside onClickOutside={onClose}>
                <div
                  className={classNames("q-SelectBody", "q-SelectBody--single")}
                  style={portal ? dropdown.current : null}
                >
                  {searchable && (
                    <div className="q-SelectBody__search">
                      <SearchIcon />
                      <input
                        type="text"
                        autoFocus={focused}
                        {...getInputProps()}
                      />
                    </div>
                  )}

                  <ol
                    className="q-SelectBody__items"
                    {...getMenuProps({ ref: registerScrollArea })}
                  >
                    {allOptions
                      .filter(
                        item =>
                          !inputValue || nomeCorresponde(inputValue, item.value)
                      )
                      .map((item, index) => (
                        <li
                          key={index}
                          ref={registerScrollTarget(item.id)}
                          className={classNames(
                            "q-SelectItem",
                            "q-SelectItem--single",
                            item.id === value && "q-SelectItem--selected",
                            highlightedIndex === index &&
                              "q-SelectItem--highlighted"
                          )}
                        >
                          <label {...getItemProps({ index, item })}>
                            {item.value}
                          </label>
                        </li>
                      ))}
                  </ol>
                </div>
              </ClickOutside>
            </SelectBody>
          )}
        </div>
      )}
    </Downshift>
  );
};
